import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import PaymentIcon from "@mui/icons-material/Payment";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";

export default function PrivacyPolicy() {
  const year = new Date().getFullYear();

  return (
    <Box component="main" sx={{ py: 8, background: "#fff", color: "#0b1220" }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Typography variant="h4" fontWeight={800}>
            Privacy Policy — FentrixBank
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            Last Updated: {year}
          </Typography>

          <Typography>
            FentrixBank ("we", "us", "our") respects your privacy and is committed to
            protecting your personal data. This policy explains what data we collect,
            why we collect it, how it is processed, who we share it with, and the
            choices available to you.
          </Typography>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            1. Information We Collect
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <AccountBoxIcon />
              </ListItemIcon>
              <ListItemText
                primary="Personal Information"
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Full name, email, phone number, date of birth, residential address,
                    government-issued ID (for KYC), and biometric data where required.
                  </Typography>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <PaymentIcon />
              </ListItemIcon>
              <ListItemText
                primary="Financial Information"
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Account numbers, transaction history, deposit/withdrawal details,
                    card information and payment method metadata.
                  </Typography>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <DeviceHubIcon />
              </ListItemIcon>
              <ListItemText
                primary="Technical & Usage Data"
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    IP address, device identifiers, browser type, cookies and analytics
                    data to improve our service and detect fraud.
                  </Typography>
                }
              />
            </ListItem>
          </List>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            2. How We Use Your Information
          </Typography>

          <Typography>
            We use personal data to provide our services securely and lawfully: to
            onboard customers (KYC), process payments, prevent fraud and money
            laundering, maintain regulatory compliance, provide support, and improve
            our products.
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <PrivacyTipIcon />
              </ListItemIcon>
              <ListItemText
                primary="Legal & Compliance"
                secondary="Fulfill legal and regulatory obligations including reporting and KYC/AML checks."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <PrivacyTipIcon />
              </ListItemIcon>
              <ListItemText
                primary="Product & Risk Management"
                secondary="Fraud detection, analytics, product improvement and dispute resolution."
              />
            </ListItem>
          </List>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            3. Sharing & Disclosures
          </Typography>

          <Typography>
            We share data only with trusted partners when necessary: payment processors,
            fraud prevention providers, legal authorities where required, and service
            providers under strict contracts. We do NOT sell your personal data.
          </Typography>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            4. Data Security & Retention
          </Typography>

          <Typography>
            We maintain industry-standard safeguards including encryption in transit and
            at rest, role-based access, logging, and continuous security monitoring.
            Data is retained only as long as necessary and as required by law.
          </Typography>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            5. Your Rights
          </Typography>

          <Typography>
            Depending on your jurisdiction, you may have rights to access, correct,
            delete, restrict processing of your data, or obtain a copy. To exercise
            rights, contact our Data Protection team at{" "}
            <strong>support@fentrixbank.com</strong>.
          </Typography>

          <Divider />

          <Typography variant="h6" fontWeight={700}>
            6. Contact
          </Typography>

          <Typography>
            For questions about privacy or to exercise your rights:{" "}
            <strong>support@fentrixbank.com</strong>
          </Typography>

          <Typography variant="caption" color="text.secondary">
            This Privacy Policy is a general summary and does not form a contract. Your
            use of FentrixBank is also subject to our Terms & Conditions.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}